import React from 'react'
import { Helmet } from 'react-helmet'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { CardGroup, Button, Row, Col, Container } from 'react-bootstrap'
import { IoMdFunnel as RefineIcon } from 'react-icons/io'
import { itemListSchema, catalogContainer, accountsContainer } from 'src/models'
import { catalogState } from 'src/models/Catalog/catalogState'
import {
  application,
  ApiContent,
  isUrlAbsolute,
  i18nTranslate,
  getLiveEventStatus,
  trackEnhancedProductViewEvent,
  trackRedesignProductViewEvent,
  parseMenuCategory,
  enablePlpRedesign,
  enablePlpBannerImage,
  enableNewFeature,
} from 'src/utils'
import filterIcon from 'src/images/filter.svg'
import { productDeps, catalogDeps } from 'src/deps'
import {
  ProductCard,
  ProductSort,
  BreadCrumb,
  EmptyProduct,
  Pagination,
  SideFilter,
  QuickView,
  ProductCompareFloatingBar,
  ProductDragAndDrop,
  CatalogComponent,
  MetaDecorator,
} from 'src/views/components'
// import { CSWrapper } from 'src/views/components/CSWrapper/CSWrapper'
import { LoadingProducts } from 'src/views/placeholders/LoadingProducts'
import {
  isMerchPLP,
  enableQuickView,
  enableProductCompare,
  APPConfig,
  deleteFromLocalStorage,
} from 'config/appConfig'
import { checkIsMobile, IS_BROWSER } from 'src/utils/application'
import { pageNames } from 'src/routes/pathParams'
import isEmpty from 'lodash/isEmpty'
import { HeroBanner } from 'src/views/studioComponents'
import './styles.scss'
import ContentStack from 'src/api/contentStack'
import { findMatchingCategory } from 'src/utils/contentStackMatcher'
import { filterIconLatest } from './fixture'

@observer
class Catalog extends CatalogComponent {
  nameSpace = 'ssr-resource'
  static contextType = ApiContent
  @observable catalogResponse = {}
  @observable refineButtonRef = React.createRef()
  @observable isLandscapeView = false
  enableLiveEvent = getLiveEventStatus()
  constructor(props, context) {
    super(props, context)
    this.state = {
      heroImageUrl: null,
      csImageTransformBaseUrl: null,
    }
  }
  getCategoryBannerImageData = async () => {
    if (!enablePlpBannerImage()) return
    if (!ContentStack || typeof ContentStack.getEntry !== 'function') return
    try {
      const locale = this.props?.locale || 'US-en'
      const csData = await ContentStack.getEntry('nuskin_header', locale)
      const propCatalogId =
        this.props?.match?.params?.catalogId ||
        this.props?.match?.params?.catalogid ||
        catalogDeps?.queryParam?.catalogId ||
        this.catalogResponse?.category?.identifier ||
        ''
      const result = findMatchingCategory(csData, propCatalogId)
      if (result?.heroImageUrl) {
        this.setState({ heroImageUrl: result.heroImageUrl })
      }
    } catch (e) {
      console.error('ContentStack.getEntry error:', e)
    }
  }
  async componentDidMount() {
    await this.getCategoryBannerImageData()
    deleteFromLocalStorage('gpsRedirection')
  }

  // Passed to SideFilter as handleFilter; without it filter clicks do nothing
  getFilter = async filterValue => {
    await catalogDeps.handleFilter(filterValue, {
      ...this.props,
    })
  }

  componentWillUnmount() {
    if (IS_BROWSER) {
      try {
        const portrait = window.matchMedia('(orientation: portrait)')
        portrait.removeEventListener('change', function () {})
      } catch (e) {
        console.warn('remove orientation event', e)
      }
    }
  }

  renderCatalogView = props => {
    const { listItem: product, index, handleSizeCalc, cardHeight } = props
    const categoryId = this.catalogResponse?.category?.identifier || ''
    const productBadge = product?.properties?.productBadge || ''
    const productPromotion = product?.promotion || []
    const selectedSKU = catalogDeps?.getDefaultSelectedSku(product) || []
    const skuId =
      (selectedSKU && selectedSKU[0] && selectedSKU[0]?.identifier) || ''
    const productDetails =
      selectedSKU[0] && (catalogDeps?.isLoadFromSKU || isMerchPLP)
        ? selectedSKU[0]
        : {
            ...product,
            ...(selectedSKU[0] && {
              prices: selectedSKU[0]?.prices,
              priceFacets: selectedSKU[0]?.priceFacets,
              totalValue: selectedSKU[0]?.totalValue,
            }),
          }
    const productType = product?.type || ''
    const productId = product?.identifier || ''
    const isProductComparable = catalogState.isProductComparable(
      this.catalogResponse,
      product
    )
    return (
      <ProductCard
        key={`product-card_${index}`}
        currency={product?.currency}
        categoryId={categoryId}
        product={productDetails}
        productId={productId}
        location={this.props?.location}
        productBadge={productBadge}
        productPromotion={productPromotion}
        productType={productType}
        isProductComparable={isProductComparable}
        isToShowPriceLabel={false}
        skuId={skuId}
        {...(isMerchPLP && {
          handleSizeCalc,
          cardStyle: { height: `${cardHeight}px` },
        })}
        isFromCatalog={true}
        index={index}
        history={this.props?.history}
      />
    )
  }

  getPlpCategoryName = (
    pageUrl,
    csCategoryOverride,
    searchTerm,
    didYouMean
  ) => {
    if (pageUrl?.includes(pageNames.search)) {
      if (didYouMean?.length > 0) {
        return (
          catalogContainer?.searchSuggestionsResponse?.didYouMean?.[0] || ''
        )
      }
      return searchTerm || ''
    }
    return (
      csCategoryOverride ||
      this.catalogResponse?.category?.properties?.name ||
      ''
    )
  }

  isFilterVisible = () => {
    if (!IS_BROWSER || !screen) return true
    const orientation =
      (screen.orientation || {}).type ||
      screen.mozOrientation ||
      screen.msOrientation
    if (
      orientation === 'landscape-primary' ||
      orientation === 'portrait-primary'
    ) {
      return false
    }
    return true
  }

  getProductDetails = (csCategoryOverride = '') => {
    const pageUrl = this.props?.location?.pathname
    if (this.catalogResponse && this.catalogResponse?.product) {
      const { facetFilter = [], sort = [] } = this.catalogResponse
      const facetPosition = this.catalogResponse?.facetPosition || ''

      let searchTermParam
      try {
        searchTermParam = decodeURIComponent(
          catalogDeps?.queryParam?.searchTerm || ''
        )
      } catch {
        searchTermParam = catalogDeps?.queryParam?.searchTerm
      }
      // console.log(
      //   'search ' +
      //     (catalogDeps?.queryParam?.searchTerm || '') +
      //     ' this.catalogResponse?.searchTerm ' +
      //     (this.catalogResponse?.searchTerm || '')
      // )
      // console.log(
      //   'search',
      //   JSON.parse(JSON.stringify(this.catalogResponse || {}))
      // )
      const searchTerm = searchTermParam
        ? searchTermParam
        : pageUrl?.includes(pageNames.search)
          ? this.catalogResponse?.searchTerm
          : ''
      const itemTotalCount = this.catalogResponse?.pageableInfo?.totalCount || 0

      const didYouMean =
        catalogContainer?.searchSuggestionsResponse?.didYouMean || []

      let categoryName = this.getPlpCategoryName(
        pageUrl,
        csCategoryOverride,
        searchTerm,
        didYouMean
      )

      const searchResultsTitle =
        itemTotalCount +
        ' ' +
        i18nTranslate('plp.searchResults', 'Search Results for', {
          nameSpace: this.nameSpace,
        })
      const searchInstead = i18nTranslate('plp.searchInstead', 'instead of ', {
        nameSpace: this.nameSpace,
      })
      const searchVal =
        (didYouMean?.length > 0 && `${searchInstead} "${searchTerm}"`) || ''
      const plpTitle = searchTerm
        ? `${searchResultsTitle} "${categoryName}" ${searchVal}`
        : categoryName || ''

      const filterShow = this.isFilterVisible()

      const currencySymbol =
        this.catalogResponse?.product?.[0]?.sku?.[0]?.prices?.[0]
          ?.currencySymbol ||
        APPConfig?.getAppConfig()?.currencySymbol ||
        '$'

      const searchTermProperty = {
        title: plpTitle,
        searchTerm: searchTerm,
        count: itemTotalCount,
        searchInsteadText: searchInstead,
        searchName: categoryName,
        didYouMeanSugg: didYouMean,
        className: '',
      }
      const mobileSearchTermProperty = {
        title: plpTitle,
        searchTerm: searchTerm,
        count: itemTotalCount,
        searchInsteadText: searchInstead,
        searchName: categoryName,
        didYouMeanSugg: didYouMean,
        className: '',
      }

      return {
        facetFilter,
        facetPosition,
        sort,
        categoryName,
        plpTitle,
        filterShow,
        currencySymbol,
        searchTermProperty,
        searchInstead,
        didYouMean,
        mobileSearchTermProperty,
      }
    }

    return {}
  }

  renderProductListView = details => {
    const {
      facetFilter,
      facetPosition,
      sort,
      categoryName,
      plpTitle,
      filterShow,
      currencySymbol,
      searchTermProperty,
      searchInstead,
      didYouMean,
      mobileSearchTermProperty,
    } = details

    if (this.enableLiveEvent === 'true') {
      trackEnhancedProductViewEvent({
        product: this.catalogResponse?.product,
        type: 'view_item_list',
      })
    }
    if (enablePlpRedesign() || enableNewFeature('enableRedesignGTMViewList')) {
      trackRedesignProductViewEvent({
        category: this.catalogResponse?.category,
        product: this.catalogResponse?.product,
        type: 'view_item_list',
        sponsorId: accountsContainer?.getAccountSponsorId() || 'nuskin',
        searchTerm: searchTermProperty?.searchTerm,
        categoryName: categoryName,
      })
    }
    catalogContainer.categoryListName.plp.categoryId = categoryName || ''
    catalogContainer.categoryListName.plp.listName =
      this.catalogResponse?.category?.parentId ||
      searchTermProperty?.searchTerm ||
      ''

    return (
      <Row
        noGutters
        className="catalog-page-wrap mx-auto my-0"
        data-testid="qa-catalog-wrapper">
        {(catalogDeps.isFilterOpen === true ||
          (application?.availHeight < application?.availWidth &&
            filterShow === true) ||
          (application?.isMobile === false && filterShow === true) ||
          (filterShow === false && IS_BROWSER && screen?.width > 767) ||
          (this.isLandscapeView === true &&
            IS_BROWSER &&
            screen?.width > 425)) &&
          this.enableLiveEvent !== 'true' && (
            <SideFilter
              facetFilter={facetFilter}
              facetPosition={facetPosition}
              filterProps={catalogDeps?.queryParam?.filter}
              handleFilter={this.getFilter}
              toggleFilter={catalogDeps?.toggleFilter}
              currencySymbol={currencySymbol}
              isFilterOpen={catalogDeps?.isFilterOpen}
              handleFilterApply={catalogDeps?.handleFilterApply}
              {...this.props}
            />
          )}
        <Col
          // lg={this.enableLiveEvent === 'true' ? 12 : 9}
          // md={this.enableLiveEvent === 'true' ? 12 : 9}
          // sm={12}
          // xs={12}
          className="plp-product-wrapper px-0 pl-md-4">
          {/* {this.renderSearchTitle(mobileSearchTermProperty)} */}
          {/* <div className="carousel-in-plp">
            <RecommendedProducts recommendationType="myRecommendedProducts" />
          </div> */}
          {catalogDeps?.showProductBlock === true && (
            <>
              {!isMerchPLP && (
                <Row
                  className="plp-header align-items-center justify-content-between py-2 ml-0 my-1"
                  noGutters>
                  {this.enableLiveEvent !== 'true' && (
                    <Button
                      className={`refine-button p-0 border-0 bg-transparent d-md-none ${enablePlpRedesign() ? 'd-none' : ''}`}
                      onClick={catalogDeps.toggleFilter}
                      variant="outline-dark"
                      aria-label="refine"
                      aria-expanded={!catalogDeps.showProductBlock}
                      ref={this.refineButtonRef}>
                      <RefineIcon className="refine-icon" />
                    </Button>
                  )}
                  {this.productCount(searchTermProperty)}

                  {this.enableLiveEvent !== 'true' && (
                    <ProductSort
                      sortOptions={sort}
                      // sortProps={catalogDeps.queryParam.sort}
                      handleSort={this.getSort}
                      {...this.props}
                    />
                  )}
                </Row>
              )}
              {/* {this.renderSearchTitle(mobileSearchTermProperty)} */}
              <ProductDragAndDrop
                useDragAndDrop={isMerchPLP}
                cardHeight={300}
                cols={3}
                refineButtonRef={this.refineButtonRef}
                list={this.catalogResponse?.product}
                renderFunction={this.renderCatalogView}
                wrapper={children => (
                  <CardGroup className={`catalog-page ml-0 ssr-catalog-page`}>
                    {children}
                  </CardGroup>
                )}
              />
            </>
          )}
          {catalogDeps?.totalPage > 1 &&
            !(catalogDeps?.isFilterOpen && application?.isMobile) && (
              <Pagination
                className="justify-content-center"
                handlePagination={this.getPagination}
                totalPages={catalogDeps?.totalPage}
                active={catalogDeps?.activePage}
              />
            )}
          {/* {searchTermProperty?.searchTerm && (
            <div className="carousel-in-plp">
              <RecommendedProducts recommendationType="getAwsMostPopularProducts" />
            </div>
          )} */}
        </Col>
      </Row>
    )
  }

  renderSearchTitle = properties => {
    return (
      <Row
        noGutters
        className={`plp-title align-items-center ${
          this.enableLiveEvent !== 'true' ? properties.className : 'd-flex'
        }`}>
        {properties.searchTerm && (
          <h1 className="category-name mr-2 mb-0" data-testid="qa-plp-title">
            <span className="search-title" data-testid="qa-plp-count">
              {properties.count}
            </span>
            &nbsp;
            <span>
              {i18nTranslate('plp.resultsFor', 'results for', {
                nameSpace: this.nameSpace,
              })}
            </span>
            &nbsp;
            <span className="search-title" data-testid="qa-plp-title">
              {'“' + properties.searchName + '”'}
            </span>
            &nbsp;
            {(properties.didYouMeanSugg?.length > 0 && (
              <>
                <span>{properties.searchInsteadText}</span>
                <span className="search-title" data-testid="qa-plp-title">
                  {'“' + properties.searchTerm + '”'}
                </span>
              </>
            )) ||
              ''}
          </h1>
        )}
        {!isEmpty(properties) && !properties.searchTerm && (
          <h1
            className="category-name mr-2 mb-0 heading-2-bold"
            data-testid="qa-plp-title">
            {parseMenuCategory(properties.title)}
          </h1>
        )}
        {enablePlpRedesign() &&
          application?.isMobile &&
          (properties.searchTerm || properties.title) && (
            <div
              className="refine-icon"
              onClick={catalogDeps.toggleFilter}
              data-testid="qa-plp-refine-icon">
              {filterIconLatest()}
            </div>
          )}
      </Row>
    )
  }

  productCount = properties => {
    if (properties?.count > 0) {
      return (
        <span className="product-count" data-testid="qa-plp-count">
          ({properties.count}{' '}
          {i18nTranslate('plp.matchesCount', 'Products', {
            nameSpace: this.nameSpace,
          })}
          )
        </span>
      )
    }
    return <></>
  }

  render() {
    // hero banner element (render under navbar, above breadcrumb)
    const heroBanner = this.state?.heroImageUrl ? (
      <section className="plp-hero-banner">
        <img src={this.state.heroImageUrl} alt="Hero banner" />
      </section>
    ) : (
      <></>
    )

    // compute showHeroBanner using application.availWidth first (safer for SSR/tests), fallback to window.innerWidth
    const screenWidth = IS_BROWSER
      ? application?.availWidth || window?.innerWidth || 0
      : 0
    const showHeroBanner =
      enablePlpRedesign() &&
      enablePlpBannerImage() &&
      IS_BROWSER &&
      screenWidth > 768

    if (IS_BROWSER && this.catalogResponse?.redirectURL) {
      if (isUrlAbsolute(this.catalogResponse?.redirectURL)) {
        window.location.href = this.catalogResponse?.redirectURL
      } else {
        window.location.href =
          window.location.origin + `${this.catalogResponse?.redirectURL}`
      }
    }
    const { category = [], status = '' } = this.catalogResponse || {}
    const pageUrl = this.props?.location?.pathname || ''
    const isSearchPage = pageUrl?.includes(pageNames.search)
    const breadCrumbList = productDeps.getBreadCrumbList(category)
    const lastCrumb = breadCrumbList?.length > 1 ? breadCrumbList.at(-1) : null
    const csCategoryName =
      !isSearchPage &&
      enableNewFeature('enableContentStackBreadcrumb') &&
      lastCrumb?.label
        ? lastCrumb.label
        : ''
    const categoryName =
      csCategoryName ||
      this.catalogResponse?.category?.properties?.name ||
      catalogDeps?.queryParam?.searchTerm ||
      this.catalogResponse?.searchTerm ||
      ''

    const title = i18nTranslate('productListPage.title', 'Nu Skin |', {
      nameSpace: this.nameSpace,
    })
    const products = this.catalogResponse?.product || []
    const productListTitle = categoryName
      ? categoryName?.replace?.(' | Nu Skin', '')?.replace?.('Nu Skin | ', '')
      : `Product Not Found`
    const metaTitle =
      parseMenuCategory(
        this.catalogResponse?.category?.properties?.meta_title
          ? this.catalogResponse?.category?.properties?.meta_title
              ?.replace?.(' | Nu Skin', '')
              ?.replace?.('Nu Skin | ', '')
          : productListTitle
      ) + ' | Nu Skin'
    const metaDescription =
      this.catalogResponse?.category?.properties?.meta_description ||
      categoryName
    // canonical url to be refered as semantics attribute in PLP response.
    const canonical = this.catalogResponse?.category?.semantics?.[0] || ''
    const schemaData = itemListSchema(products)
    const productDetails =
      this.getProductDetails(isSearchPage ? '' : csCategoryName) || {}
    const enableNewPDPStyle =
      APPConfig?.getAppConfig()?.enableNewPDPStyle === 'true'
    if (status === 'failure' || (status !== '' && products?.length === 0)) {
      const searchValue = this.props?.match?.params?.searchTerm || ''
      let searchTerm
      try {
        searchTerm = decodeURIComponent(`"${searchValue}"`)
      } catch {
        searchTerm = `"${searchValue}"`
      }
    }
    return (
      <>
        <MetaDecorator
          title={metaTitle}
          imageUrl=""
          imageAlt=""
          description={metaDescription}
          canonicalUrl={canonical}
          location={this.props}
        />
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
          {enableNewPDPStyle && (
            <link
              href={
                'https://fonts.googleapis.com/css2?family=Lora:wght@400;700'
              }
              rel="stylesheet"
            />
          )}
          {enableNewPDPStyle && (
            <link
              href={
                'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800'
              }
              rel="stylesheet"
            />
          )}
        </Helmet>

        {enableProductCompare && <ProductCompareFloatingBar />}

        {showHeroBanner && heroBanner}
        <BreadCrumb
          breadCrumbList={breadCrumbList}
          location={this.props?.location}
        />
        <section
          className={`main-catalog-page container-fluid ${
            this.enableLiveEvent === 'true' ? 'live-event-enabled' : ''
          } ${application?.isMobile && enablePlpRedesign() ? 'pt-4' : ''}`}>
          {enableQuickView && !isMerchPLP && <QuickView {...this.props} />}
          {this.enableLiveEvent !== 'true' && (
            <>
              <div
                className={`plp-title-container ${enablePlpRedesign() ? 'plp-redesign' : ''}`}>
                {this.renderSearchTitle(
                  productDetails.mobileSearchTermProperty || {}
                )}
              </div>
            </>
          )}

          <Container
            className={`p-0 plp-page ${enablePlpRedesign() ? 'plp-redesign' : ''}`}>
            {IS_BROWSER && catalogContainer.shouldShowPlaceholder === true ? (
              <LoadingProducts />
            ) : (
              <>
                {status === 'failure' ||
                (status !== '' && products?.length === 0) ? (
                  <EmptyProduct {...this.props} />
                ) : (
                  Object.keys(productDetails || {}).length > 0 &&
                  this.renderProductListView(productDetails)
                )}
              </>
            )}
          </Container>
        </section>
      </>
    )
  }
}

export { Catalog }
// export default Catalog
export default Catalog
